import { useState, useMemo } from "react";
import { Shield, Globe, MoreHorizontal, Info, AlertCircle, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { NetworkRuleDetail } from "./NetworkRuleDetail";
import { useLevelRuleScope } from "@/hooks/useLevelRuleScope";
import { AppIcon } from "./AppIcon";
import { AppIdDisplay } from "./AppIdDisplay";
import { useAppCache } from "@/hooks/useAppCache";
import { useNetworkRulesWithScope } from "@/hooks/useNetworkRulesWithScope";

interface LevelNetworkRulesTableProps {
  levelId: string;
  ruleType?: "system-wide" | "app-specific";
  
}

interface GroupedAppRule {
  parentApp: {
    id: string;
    name: string;
    iconUrl?: string;
    bundleId: string;
  };
  rules: any[];
}

interface NetworkRuleWithScopeInfo {
  id: string;
  name: string;
  description?: string;
  type: "App Specific" | "General";
  category: "app-specific" | "system-wide";
  bundleId?: string;
  appName?: string;
  lastModified: string;
  urls: string[];
  ruleType: "webkit" | "socket";
  function?: boolean;
  parentApp?: {
    id: string;
    name: string;
    iconUrl?: string;
    bundleId: string;
  };
  scope: {
    users: number;
    devices: number;
    groups: string[];
  };
}

export const LevelNetworkRulesTable = ({ levelId, ruleType }: LevelNetworkRulesTableProps) => {
  const [selectedRule, setSelectedRule] = useState<NetworkRuleWithScopeInfo | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
  
  
  const { getApp, getCachedApp, triggerFetch } = useAppCache();
  
  // Get the app filter for this level (use proper format)
  const appFilter = { "default": true as const };
  const { scopedRuleIds, rulesData, isLoading, hasError, errorMessage } = useLevelRuleScope(levelId, appFilter);

  console.log('LevelNetworkRulesTable - Status:', {
    hasError,
    errorMessage,
    rulesDataType: typeof rulesData,
    rulesDataLength: Array.isArray(rulesData) ? rulesData.length : 'not array'
  });

  // Transform the level rules data to NetworkRule format with scope information
  const transformedRules = useMemo(() => {
    // If there's an error from the scope hook, return empty array
    if (hasError) {
      console.log('LevelNetworkRulesTable - Returning empty array due to scope error');
      return [];
    }

    if (!rulesData || rulesData.length === 0) {
      return [];
    }

    // The rulesData is already flattened from the hook
    const flatRules = rulesData;

    console.log('Processing flattened level-specific rules data:', flatRules.length, 'rules');
    console.log('LevelNetworkRulesTable - Sample rule structure:', flatRules[0]);
    
    // Extract parent app IDs and trigger fetching
    const parentAppIds = flatRules
      .filter((rule: any) => rule.parent)
      .map((rule: any) => rule.parent);
    
    if (parentAppIds.length > 0) {
      console.log('Triggering fetch for parent apps:', parentAppIds);
      triggerFetch(parentAppIds);
    }
    
    const transformed: NetworkRuleWithScopeInfo[] = flatRules.map((rule: any) => {
      console.log('Processing rule:', rule);
      
      // Calculate relative time for lastModified
      const updatedAt = new Date(rule._creationTime || Date.now());
      const now = new Date();
      const diffInHours = Math.abs(now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60);
      
      let lastModified: string;
      if (diffInHours < 24) {
        if (diffInHours < 1) {
          lastModified = "Just now";
        } else {
          lastModified = `${Math.floor(diffInHours)} hour${Math.floor(diffInHours) !== 1 ? 's' : ''} ago`;
        }
      } else {
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) {
          lastModified = `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
        } else {
          const diffInWeeks = Math.floor(diffInDays / 7);
          lastModified = `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
        }
      }

      // Determine if rule is app-specific based on parent field
      const isAppSpecific = !!rule.parent;
      const type: "App Specific" | "General" = isAppSpecific ? "App Specific" : "General";
      const category: "app-specific" | "system-wide" = isAppSpecific ? "app-specific" : "system-wide";

      // Handle parent app information
      let parentApp = undefined;
      
      if (rule.parent) {
        const parentAppData = getApp(rule.parent);
        
        if (parentAppData) {
          parentApp = {
            id: parentAppData.id,
            name: parentAppData.name,
            iconUrl: parentAppData.iconUrl,
            bundleId: parentAppData.bundleId || ''
          };
        }
      }

      const transformedRule: NetworkRuleWithScopeInfo = {
        id: rule._id,
        name: rule.name || 'Unnamed Rule',
        description: rule.description || '',
        type,
        category,
        bundleId: rule.bundleId || '',
        appName: rule.name || 'Unnamed Rule',
        lastModified,
        urls: rule.urls || [],
        ruleType: rule.type || 'webkit',
        
        scope: {
          users: 0,
          devices: 0,
          groups: []
        },
        parentApp
      };

      console.log('Transformed rule:', transformedRule);
      return transformedRule;
    });

    console.log('Final transformed level rules:', transformed.length, 'rules processed');
    
    return transformed;
  }, [rulesData, getApp, hasError]);

  // Get global rules that apply to all devices (using the same hook as device tables)
  const { data: allNetworkRules = [] } = useNetworkRulesWithScope();

  // Combine level-specific rules with global rules
  const combinedRules = useMemo(() => {
    // Transform global rules to match our format
    const globalRules = allNetworkRules.map(rule => ({
      id: rule.id,
      name: rule.name,
      description: rule.description,
      type: rule.type as "App Specific" | "General",
      category: rule.category,
      bundleId: rule.bundleId || '',
      appName: rule.appName || rule.name,
      lastModified: rule.lastModified,
      urls: rule.urls,
      ruleType: rule.ruleType,
      
      parentApp: rule.parentApp,
      scope: rule.scope
    }));

    // Combine transformed level rules with global rules, avoiding duplicates
    const levelRuleIds = new Set(transformedRules.map(rule => rule.id));
    const uniqueGlobalRules = globalRules.filter(rule => !levelRuleIds.has(rule.id));
    
    return [...transformedRules, ...uniqueGlobalRules];
  }, [transformedRules, allNetworkRules]);

  // Filter combined rules based on type and purpose filter
  const scopedRules = combinedRules?.filter(rule => {
    console.log('Filtering rule:', { id: rule.id, name: rule.name, parentApp: rule.parentApp, ruleType });
    
    // First filter by rule type (system-wide vs app-specific)
    let typeMatch = true;
    if (ruleType === "system-wide") {
      // System-wide rules are those without a parent app
      typeMatch = !rule.parentApp;
    } else if (ruleType === "app-specific") {
      // App-specific rules are those with a parent app
      typeMatch = !!rule.parentApp;
    }
    
    return typeMatch;
  }) || [];

  console.log('LevelNetworkRulesTable - Filtered scoped rules:', scopedRules.length, scopedRules);

  const groupedByParentApp = (rules: NetworkRuleWithScopeInfo[]): GroupedAppRule[] => {
    const groupsMap = new Map<string, GroupedAppRule>();

    rules.forEach(rule => {
      if (rule.parentApp) {
        const key = rule.parentApp.id;
        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            parentApp: rule.parentApp,
            rules: []
          });
        }
        groupsMap.get(key)!.rules.push(rule);
      }
    });

    return Array.from(groupsMap.values());
  };

  const handleRuleClick = (rule: NetworkRuleWithScopeInfo, event: React.MouseEvent) => {
    setSelectedRule(rule);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedRule(null);
  };

  const handleAppToggle = (appId: string) => {
    const newExpanded = new Set(expandedApps);
    if (newExpanded.has(appId)) {
      newExpanded.delete(appId);
    } else {
      newExpanded.add(appId);
    }
    setExpandedApps(newExpanded);
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center space-y-2">
          <div className="text-gray-500">Loading network rules...</div>
        </div>
      </div>
    );
  }

  // Show error state if there's an issue with the level ID or data integrity
  if (hasError) {
    const errorMsg = errorMessage || 'Unable to load network rules for this level. This may be due to data integrity issues where the level references entities that no longer exist.';
      
    return (
      <Card>
        <CardContent className="p-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {errorMsg}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (scopedRules.length === 0) {
    const IconComponent = ruleType === "system-wide" ? Globe : Shield;
    const emptyMessage = ruleType === "system-wide" 
      ? "No system-wide network rules are currently assigned to this level."
      : "No app-specific network rules are currently assigned to this level.";
    
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <IconComponent className="w-12 h-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No {ruleType === "system-wide" ? "System-Wide" : "App-Specific"} Rules
          </h3>
          <p className="text-gray-500 text-center">
            {emptyMessage}
          </p>
        </CardContent>
      </Card>
    );
  }

  // For app-specific rules, group by parent app
  if (ruleType === "app-specific") {
    const parentAppGroups = groupedByParentApp(scopedRules);

    return (
      <>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[400px]">App / Rule</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parentAppGroups.map((group) => (
                  <>
                    {/* Parent App Header Row */}
                    <TableRow 
                      key={`app-${group.parentApp.id}`}
                      className="hover:bg-gray-50 cursor-pointer transition-colors bg-gray-25 h-12"
                      onClick={() => handleAppToggle(group.parentApp.id)}
                    >
                      <TableCell className="py-2">
                        <div className="flex items-center">
                          {expandedApps.has(group.parentApp.id) ? (
                            <ChevronDown className="w-4 h-4 mr-2 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 mr-2 text-gray-500" />
                          )}
                          <AppIdDisplay appId={group.parentApp.id} />
                        </div>
                       </TableCell>
                       
                       <TableCell className="py-2">
                         <button 
                           className="text-gray-400 hover:text-gray-600"
                           onClick={(e) => {
                             e.stopPropagation();
                             console.log("More options clicked for app");
                           }}
                         >
                           <MoreHorizontal className="w-5 h-5" />
                         </button>
                       </TableCell>
                    </TableRow>

                    {/* Expanded Rules */}
                    {expandedApps.has(group.parentApp.id) && group.rules.map((rule) => {
                      return (
                        <TableRow 
                          key={`rule-${rule.id}`}
                          className="hover:bg-gray-50 cursor-pointer transition-colors h-10"
                          onClick={(e) => handleRuleClick(rule, e)}
                        >
                           <TableCell className="py-1">
                             <div className="flex items-center pl-8">
                               <div className="flex items-center gap-2">
                                 <div>
                                   <div className="font-medium text-gray-900 text-sm">{rule.name}</div>
                                 </div>
                                 {rule.description && (
                                   <TooltipProvider>
                                     <Tooltip>
                                       <TooltipTrigger asChild>
                                         <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                       </TooltipTrigger>
                                       <TooltipContent>
                                         <p className="max-w-xs">{rule.description}</p>
                                       </TooltipContent>
                                     </Tooltip>
                                   </TooltipProvider>
                                 )}
                               </div>
                             </div>
                           </TableCell>
                           <TableCell className="py-1">
                            <button 
                              className="text-gray-400 hover:text-gray-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log("More options clicked for rule");
                              }}
                            >
                              <MoreHorizontal className="w-5 h-5" />
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                ))}
                
                 {parentAppGroups.length === 0 && (
                   <TableRow>
                     <TableCell colSpan={2} className="text-center py-8 text-gray-500">
                       No app-specific rules available for this level
                     </TableCell>
                   </TableRow>
                 )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Rule Detail Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-6xl h-[80vh] p-0">
            {selectedRule && (
              <NetworkRuleDetail 
                rule={selectedRule as any} 
                onBack={handleCloseDialog}
                onDelete={() => {
                  handleCloseDialog();
                }}
                ruleIdOverride={selectedRule?.id}
              />
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // For system-wide rules, show the flat table
  return (
    <>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">Rule Name</TableHead>
                
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scopedRules.map((rule) => {
                return (
                  <TableRow 
                    key={rule.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors h-10"
                    onClick={(e) => handleRuleClick(rule, e)}
                  >
                     <TableCell className="py-2">
                       <div className="flex items-center">
                         <div className="flex items-center gap-2">
                           <div>
                             <div className="font-medium text-gray-900 text-sm">{rule.name}</div>
                           </div>
                           {rule.description && (
                             <TooltipProvider>
                               <Tooltip>
                                 <TooltipTrigger asChild>
                                   <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                 </TooltipTrigger>
                                 <TooltipContent>
                                   <p className="max-w-xs">{rule.description}</p>
                                 </TooltipContent>
                               </Tooltip>
                             </TooltipProvider>
                           )}
                         </div>
                       </div>
                     </TableCell>
                     <TableCell className="py-2">
                      <button 
                        className="text-gray-400 hover:text-gray-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log("More options clicked for rule");
                        }}
                      >
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Rule Detail Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-6xl h-[80vh] p-0">
          {selectedRule && (
            <NetworkRuleDetail 
              rule={selectedRule as any} 
              onBack={handleCloseDialog}
              onDelete={() => {
                handleCloseDialog();
              }}
              ruleIdOverride={selectedRule?.id}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
